import { useEffect, useMemo, useState, useCallback, memo } from 'react';
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import Flex from 'legacyfrontend/components/Flex';
import FlightDetailDialog from './FlightDetailDialog';
import TooltipLuggage from './TooltipLuggage';
import formatSecondsToHours from 'legacyfrontend/utils/formatSecondsToHours';
import { formatDate, formatTime } from 'legacyfrontend/utils/dateUtils';
import TooltipScale from './TooltipScale';
import { useColors } from 'legacyfrontend/hooks/useColors';
const checkboxStyles = { margin: 0, padding: 0 };

const FlightInfoRow = memo(({ flight, kind, isSelected, onSelect, isBookingPage = false }) => {
  const { background, gray, coral } = useColors();
  const [scales, setScales] = useState([]);

  const flightData = useMemo(() => {
    const isMultiFlight = Array.isArray(flight) && flight.length > 0;
    const firstFlight = isMultiFlight ? flight[0] : flight;
    const lastFlight = isMultiFlight ? flight[flight.length - 1] : flight;

    return {
      departureDate: firstFlight.departureDate,
      arrivalDate: lastFlight.arrivalDate,
      departureCity: firstFlight.departure.city,
      arrivalCity: lastFlight.arrival.city,
      hasScales: isMultiFlight && flight.length > 1,
      originId: firstFlight.originDestinationReferences.slice(0, 3),
      destinationId: firstFlight.originDestinationReferences.slice(3, 6),
      duration: firstFlight.duration,
    };
  }, [flight]);

  const baggage = useMemo(() => [...new Set(flight.map((flight) => flight.baggage))], [flight]);

  const airlineLogos = useMemo(() => {
    return [...new Set(flight.map((flight) => flight.logoCompany))].filter(Boolean);
  }, [flight]);

  const calculateScales = useCallback((flights) => {
    if (!Array.isArray(flights) || flights.length <= 1) {
      setScales([{ isNotScale: true }, { isNotScale: true }]);
      return;
    }

    const newScales = [{ isNotScale: true }];

    flights.slice(0, -1).forEach((currentFlight, index) => {
      const nextFlight = flights[index + 1];
      const waitTime = Math.max(
        (new Date(nextFlight.departureDate) - new Date(currentFlight.arrivalDate)) / 1000,
        0
      );

      newScales.push({
        id: `scale${index + 1}`,
        origin: `${currentFlight.arrival.name}, ${currentFlight.arrival.city}`,
        destination: `${nextFlight.arrival.name}, ${nextFlight.arrival.city}`,
        samePlane: false,
        technicalStop: false,
        sameAirport: currentFlight.arrival.name === nextFlight.departure.name,
        waitTime,
        isNotScale: false,
      });
    });

    newScales.push({ isNotScale: true });
    setScales(newScales);
  }, []);

  useEffect(() => {
    calculateScales(flight);
  }, [flight, calculateScales]);

  return (
    <Flex className='flight_card__data'>
      {!isBookingPage && (
        <div
          className='flight_card_info_first_row'
          style={{
            borderLeft: `10px solid ${isSelected ? background : gray}`,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isSelected}
                onChange={onSelect}
                sx={{
                  '&.Mui-checked': {
                    color: background,
                  },
                }}
              />
            }
            sx={checkboxStyles}
          />
        </div>
      )}

      <Stack
        className='flight_card_info_second_row'
        sx={{
          flexDirection: { xs: 'column', lg: 'row' },
          marginTop: { xs: '1rem' },
          ...(isBookingPage && {
            pl: 2,
          }),
        }}
      >
        {/* Airline Logos */}
        {airlineLogos.length > 0 && (
          <Stack
            sx={{
              marginRight: { xs: 0, lg: 2 },
              marginBottom: { xs: 2, lg: 0 },
              justifyContent: 'center',
            }}
          >
            {airlineLogos.map((logoUrl, index) => (
              <img
                alt='Airline Logo'
                key={`logo-${index}`}
                src={logoUrl}
                style={{
                  maxWidth: '100px',
                  width: '100%',
                  height: 'auto',
                  marginTop: index > 0 ? '1rem' : 0,
                }}
              />
            ))}
          </Stack>
        )}

        <Stack
          className='flight_card_info'
          sx={{
            flexDirection: 'column',
            paddingLeft: { xs: 0, lg: '1rem' },
            paddingRight: { xs: 0, lg: '1rem' },
          }}
        >
          <Flex className='flight_card_data_jcsb'>
            <Typography fontSize='1rem' fontWeight={700}>
              {formatTime(flightData.departureDate)}
            </Typography>
            <Typography color={coral} fontSize='0.75rem'>
              {!flightData.hasScales
                ? 'Vuelo Directo'
                : `${scales.length - 2} ${scales.length > 3 ? 'escalas' : 'escala'}`}
            </Typography>
            <Typography fontSize='1rem' fontWeight={700}>
              {formatTime(flightData.arrivalDate)}
            </Typography>
          </Flex>

          <Flex
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <div className='dashed-line' />
            <div
              style={{
                display: 'flex',
                flex: 1,
                justifyContent: 'space-between',
                position: 'relative',
              }}
            >
              {scales.map((scale, index) => (
                <TooltipScale escala={scale} isNotScale={scale.isNotScale} key={`scale-${index}`} />
              ))}
            </div>
          </Flex>

          <Flex className='flight_card_data_jcsb'>
            <p className='bottom_label_flight'>{flightData.originId}</p>
            <p className='bottom_label_flight'>{flightData.destinationId}</p>
          </Flex>

          <Flex className='flight_card_data_jcsb'>
            <p className='location_text'>{flightData.departureCity}</p>
            <p className='location_text'>{flightData.arrivalCity}</p>
          </Flex>
        </Stack>
      </Stack>
      <Stack
        className='flight_card_info_three_row'
        sx={{
          flexDirection: { xs: 'column-reverse', lg: 'row' },
          paddingTop: { xs: 2, lg: 'inherit' },
          minWidth: { xs: '30%', lg: 'auto' },
          justifyContent: { xs: 'flex-end', lg: 'center' },
        }}
      >
        <Stack flex={1} gap={1} justifyContent='center' sx={{ height: { xs: '94px', lg: 'auto' } }}>
          <TooltipLuggage checkedBag={baggage[0]} />
          <Stack flexDirection='column' textAlign='center'>
            <Typography sx={{ fontSize: 14, fontWeight: 300 }}>Duración:</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 300 }}>
              {formatSecondsToHours(flightData.duration)}
            </Typography>
          </Stack>
        </Stack>

        <Stack className='flight_card_info_four_row' sx={{ padding: { xs: 2 } }}>
          <FlightDetailDialog
            destinationId={flightData.destinationId}
            flight={flight}
            kind={kind}
            originId={flightData.originId}
            scales={scales}
          />
        </Stack>
      </Stack>
    </Flex>
  );
});

export default FlightInfoRow;
